import { useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { copyElements, diffGraph, getContentOrigin, isEmptyGraphDiff, pasteElements } from 'Custom-Mindmap';
import { MindMapContext } from '../../Context/contex';
import useSaveGraphDiff from '../UndoRedo/useSaveGraphDiff';
import { connectingEdge, createNode, insertPosition, newEdgeId, newNodeId, sizeOfNode } from './newElements';

// How far (flow px) each paste or duplicate lands from the copied nodes.
const PASTE_OFFSET = 30;
const HALF = 0.5;
// Shape created by "Insert node here" on the empty canvas.
const DEFAULT_INSERT_TYPE = 'rectangle';

const deselect = (nodes) => nodes.map((node) => (node.selected ? { ...node, selected: false } : node));

// Menu and shortcut actions on the map (D-037). Each one is applied, recorded for undo, saved and broadcast.
const useMapActions = () => {
    const { documentId } = useParams();
    const {
        nodes, edges, setNodes, setEdges, recordChange, setIsDocumentChanged,
        clipboard, setClipboard, userInfo, userColor,
    } = MindMapContext();
    const save = useSaveGraphDiff();

    const commit = useCallback((after) => {
        const before = { nodes, edges };
        const diff = diffGraph(before, after);
        if (isEmptyGraphDiff(diff)) return;
        setNodes(after.nodes);
        setEdges(after.edges);
        recordChange(before, after);
        setIsDocumentChanged(true);
        save(diff, after);
    }, [nodes, edges, setNodes, setEdges, recordChange, setIsDocumentChanged, save]);

    // Copies become the current user's nodes and are selected, so they can be moved at once.
    const addCopies = useCallback((content, offset) => {
        const pasted = pasteElements(content, { offset, newNodeId, newEdgeId });
        const copies = pasted.nodes.map((node) => ({
            ...node,
            creatorId: userInfo.userId,
            data: { ...node.data, id: node.id, allocatedColor: userColor },
            selected: true,
        }));
        const copiedEdges = pasted.edges.map((edge) => ({ ...edge, documentId }));
        commit({ nodes: [...deselect(nodes), ...copies], edges: [...edges, ...copiedEdges] });
    }, [nodes, edges, commit, userInfo.userId, userColor, documentId]);

    const copy = useCallback((nodeIds) => {
        const content = copyElements(nodes, edges, nodeIds);
        if (content.nodes.length > 0) setClipboard({ content, pastes: 0 });
    }, [nodes, edges, setClipboard]);

    // With `point` (flow position) the copied nodes' top-left corner lands there; without, a step further each time.
    const paste = useCallback((point) => {
        const origin = clipboard && getContentOrigin(clipboard.content);
        if (!origin) return;
        const pastes = clipboard.pastes + 1;
        const offset = point
            ? { x: point.x - origin.x, y: point.y - origin.y }
            : { x: PASTE_OFFSET * pastes, y: PASTE_OFFSET * pastes };
        setClipboard({ ...clipboard, pastes });
        addCopies(clipboard.content, offset);
    }, [clipboard, setClipboard, addCopies]);

    const duplicate = useCallback((nodeIds) => {
        const content = copyElements(nodes, edges, nodeIds);
        if (content.nodes.length > 0) addCopies(content, { x: PASTE_OFFSET, y: PASTE_OFFSET });
    }, [nodes, edges, addCopies]);

    // A node of the same shape next to `node`, connected to it (like the "+" buttons).
    const insertNode = useCallback((node, side) => {
        const added = createNode({ type: node.type, position: insertPosition(node, side), userInfo, userColor });
        commit({ nodes: [...nodes, added], edges: [...edges, connectingEdge(node, added, side, documentId)] });
    }, [nodes, edges, commit, userInfo, userColor, documentId]);

    // A new node centred on `point` (flow position).
    const insertNodeAt = useCallback((point) => {
        const draft = createNode({ type: DEFAULT_INSERT_TYPE, position: point, userInfo, userColor });
        const { width, height } = sizeOfNode(draft);
        const added = { ...draft, position: { x: point.x - width * HALF, y: point.y - height * HALF } };
        commit({ nodes: [...nodes, added], edges });
    }, [nodes, edges, commit, userInfo, userColor]);

    // Removes the nodes with their edges, and the given edges.
    const remove = useCallback((nodeIds, edgeIds = []) => {
        const removedNodes = new Set(nodeIds);
        const removedEdges = new Set(edgeIds);
        commit({
            nodes: nodes.filter((node) => !removedNodes.has(node.id)),
            edges: edges.filter((edge) =>
                !removedEdges.has(edge.id) && !removedNodes.has(edge.source) && !removedNodes.has(edge.target)),
        });
    }, [nodes, edges, commit]);

    // The node itself, or every selected node when it is part of the selection.
    const targetIds = useCallback((node) => {
        const selected = nodes.filter((candidate) => candidate.selected).map((candidate) => candidate.id);
        return selected.includes(node.id) ? selected : [node.id];
    }, [nodes]);

    const selectedIds = useCallback(() => nodes.filter((node) => node.selected).map((node) => node.id), [nodes]);

    return {
        copy, paste, duplicate, insertNode, insertNodeAt, remove, targetIds, selectedIds,
        canPaste: Boolean(clipboard?.content.nodes.length),
    };
};

export default useMapActions;
